import type { ReactElement } from 'react';
import { useEffect, useRef } from 'react';

import { Box, Row } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { useAuth } from '@opendoor/auth-fe/context';

/**
 * Reserves the bar's own height at the foot of the page. Without it the bar paints over whatever
 * sits at the bottom of the viewport, including a control that has just taken focus.
 */
const useReservedSpace = (barRef: { current: HTMLElement | null }): void => {
  useEffect(() => {
    const node = barRef.current;
    if (!node) {
      return undefined;
    }

    document.body.style.paddingBottom = `${node.offsetHeight}px`;
    return () => {
      document.body.style.paddingBottom = '';
    };
  }, [barRef]);
};

/**
 * Marks a session an employee started through admin impersonation, so they can tell someone else's
 * dashboard from their own.
 */
export const ImpersonationToolbar = (): ReactElement | null => {
  const { authentication } = useAuth();
  const barRef = useRef<HTMLElement>(null);

  useReservedSpace(barRef);

  if (authentication.state !== 'authenticated' || !authentication.user.impersonated) {
    return null;
  }

  const { email, fullName } = authentication.user;

  return (
    <Box
      as="section"
      aria-label="Impersonated session"
      className={cn(
        // Above every sticky surface in cosmos, including the homes filters panel at z-15000, and
        // below the atoms overlay root at z-100000 so dialogs still cover it.
        'fixed bottom-0 left-0 z-[20000]',
      )}
      ref={barRef}
    >
      <Row
        className={cn(
          // Amber marks an internal session across our tooling, so this reads as family with
          // apptoolbox's deploy-preview bar. The ink is pinned too, so a theme cannot invert it
          // against the fixed background.
          'w-screen items-center justify-between gap-3 overflow-hidden whitespace-nowrap bg-amber-400 px-4 py-2',
        )}
      >
        <Row className="min-w-0 items-center gap-3">
          <Text as="span" className="min-w-0 truncate text-label-sm text-neutral-900">
            Viewing as {fullName}
          </Text>
          <Text as="span" className="min-w-0 truncate text-body-sm text-neutral-900 max-sm:hidden">
            {email}
          </Text>
        </Row>
        <Text as="span" className="shrink-0 text-body-sm text-neutral-900 max-md:hidden">
          Changes you make are saved to their account
        </Text>
      </Row>
    </Box>
  );
};
